import React from 'react';
import { useSearchParams } from 'react-router';
import { Accordions, Form, useApi, TaxonomyUtil } from '@axiom/ui';
import type { Candidate, CandidateWorkFeedFormFilter } from '@axiom/validation';
import { CandidateWorkFeedFormFilterSchema } from '@axiom/validation';

import { WorkFeedFilterUtil } from '../../utils/work-feed-filter-util';
import { WorkFeedFilterAccordion } from '../WorkFeedFilterAccordion/WorkFeedFilterAccordion';
import type { Option } from '../../types/work-feed.types';
import { TaxonomyApi } from '../../api/taxonomy-api';

const {
  countryOptions,
  engagementLengthOptions,
  opportunityStageOptions,
  weeklyCommitmentOptions,
  worksiteOptions,
} = WorkFeedFilterUtil;

export const WorkFeedFiltersForm = ({
  candidate,
  filters,
  minimal,
  name = 'WORK_FEED_FILTERS_FORM',
}: {
  candidate: Candidate;
  filters: CandidateWorkFeedFormFilter;
  minimal?: boolean;
  name?: string;
}) => {
  const [, setSearchParams] = useSearchParams();
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const practiceAreaOptions = TaxonomyUtil.getPracticeAreaOptions(
    taxonomy.practiceAreas
  );
  const focusAreaOptions = TaxonomyUtil.getFocusAreaOptions(
    taxonomy.focusAreas
  );
  const industryOptions = TaxonomyUtil.getFilteredIndustryOptions(
    taxonomy.industries
  );
  const legalRoleOptions = TaxonomyUtil.getBusinessTitleOptions(
    taxonomy.businessTitles
  );

  const isOptionsExpanded = (
    isExpanded: boolean,
    id: string,
    options: Option[],
    maxChildrenShown: number
  ) => {
    if (isExpanded) {
      return isExpanded;
    }
    const index = options.findIndex(option => option.value === id);
    return index >= maxChildrenShown;
  };

  return (
    <Form
      name={name}
      schema={CandidateWorkFeedFormFilterSchema}
      initialValues={filters}
      submitOnChange
      onSubmit={async changedData => {
        return Promise.resolve().then(() => {
          WorkFeedFilterUtil.applyFilters(
            setSearchParams,
            candidate,
            taxonomy,
            {
              ...filters,
              ...changedData,
            }
          );
        });
      }}
    >
      {({ values }) => (
        <Accordions name="WORK_FEED_FILTERS_ACCORDIONS" minimal={minimal}>
          <WorkFeedFilterAccordion
            count={values.opportunityPhases?.length ?? 0}
            expandAccordion={!!filters.opportunityPhases?.length}
            minimal={minimal}
            name="opportunityPhases"
            options={opportunityStageOptions}
            title="Opportunity Stage"
          />
          <WorkFeedFilterAccordion
            count={values.practiceAreas?.length ?? 0}
            expandAccordion={!!filters.practiceAreas?.length}
            expandOptions={filters.practiceAreas?.reduce(
              (isExpanded, id) =>
                isOptionsExpanded(isExpanded, id, practiceAreaOptions, 6),
              false
            )}
            maxChildrenShown={6}
            minimal={minimal}
            name="practiceAreas"
            options={practiceAreaOptions}
            title="Practice Area"
          />
          <WorkFeedFilterAccordion
            count={values.focusAreas?.length ?? 0}
            expandAccordion={!!filters.focusAreas?.length}
            expandOptions={filters.focusAreas?.reduce(
              (isExpanded, id) =>
                isOptionsExpanded(isExpanded, id, focusAreaOptions, 6),
              false
            )}
            maxChildrenShown={6}
            minimal={minimal}
            name="focusAreas"
            options={focusAreaOptions}
            title="Focus Area"
          />
          <WorkFeedFilterAccordion
            count={values.industries?.length ?? 0}
            expandAccordion={!!filters.industries?.length}
            expandOptions={filters.industries?.reduce(
              (isExpanded, id) =>
                isOptionsExpanded(isExpanded, id, industryOptions, 6),
              false
            )}
            maxChildrenShown={6}
            minimal={minimal}
            name="industries"
            options={industryOptions}
            title="Industry"
          />
          <WorkFeedFilterAccordion
            count={values.timeCommitment?.length ?? 0}
            expandAccordion={!!filters.timeCommitment?.length}
            minimal={minimal}
            name="timeCommitment"
            options={weeklyCommitmentOptions}
            title="Weekly Commitment"
          />
          <WorkFeedFilterAccordion
            count={values.countries?.length ?? 0}
            expandAccordion={!!filters.countries?.length}
            minimal={minimal}
            name="countries"
            options={countryOptions}
            title="Country"
          />
          <WorkFeedFilterAccordion
            count={values.worksites?.length ?? 0}
            expandAccordion={!!filters.worksites?.length}
            minimal={minimal}
            name="worksites"
            options={worksiteOptions}
            title="Worksite"
          />
          <WorkFeedFilterAccordion
            count={values.engagementLength?.length ?? 0}
            expandAccordion={!!filters.engagementLength?.length}
            minimal={minimal}
            name="engagementLength"
            options={engagementLengthOptions}
            title="Engagement Length"
          />
          <WorkFeedFilterAccordion
            count={values.businessTitles?.length ?? 0}
            expandAccordion={!!filters.businessTitles?.length}
            minimal={minimal}
            name="businessTitles"
            options={legalRoleOptions}
            title="Legal Role"
          />
        </Accordions>
      )}
    </Form>
  );
};
